import { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail, websiteChannelHeaders } from "../lib/api";
import { Bot, MessageSquare, Send, Loader2, CheckCircle2, Sparkles } from "lucide-react";

import { useTranslation } from 'react-i18next';
function formatTime(value) {
  try {
    return new Date(value).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

export default function SupportChatPanel({ productId = "max-os-cloud", compact = false }) {
  const { t } = useTranslation();
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const endRef = useRef(null);

  const fetchThread = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/support/chat", { params: { product_id: productId }, headers: websiteChannelHeaders("website-support") });
      setThread(res.data.thread);
      setMessages(Array.isArray(res.data.messages) ? res.data.messages : []);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(formatApiErrorDetail(detail || e?.message));
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    fetchThread();
  }, [fetchThread]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, loading, sending]);

  const sendMessage = async (messageText) => {
    const text = (messageText ?? input).trim();
    if (!text || sending) return;
    setSending(true);
    setError("");
    try {
      const res = await api.post("/support/chat", { product_id: productId, message: text, channel_origin: "website-support" }, { headers: websiteChannelHeaders("website-support") });
      const newMessages = Array.isArray(res.data.messages) ? res.data.messages : [];
      setMessages((prev) => [...prev, ...newMessages]);
      setThread((prev) => ({ ...(prev || {}), thread_id: res.data.thread_id, message_count: (prev?.message_count || 0) + newMessages.length }));
      setInput("");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(formatApiErrorDetail(detail || e?.message));
    } finally {
      setSending(false);
    }
  };

  const connectedAgent = thread?.agent;
  const supportGuidance = [
    "Include your name and email if you are starting a new conversation.",
    "Use the quick contact cards above for email, Telegram, WhatsApp, or Discord.",
    "This thread stays as the main record for support follow-up.",
  ];

  return (
    <section className="border border-zinc-800 bg-gradient-to-br from-zinc-950 to-zinc-950/40 overflow-hidden">
      <div className="border-b border-zinc-800 px-5 py-4 flex items-center justify-between gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-500 mb-1">// SUPPORT CHAT</div>
          <h2 className="font-display text-xl uppercase">{t('support_chat_panel.support_chat')}</h2>
          <p className="mt-2 text-sm text-zinc-400">{t('support_chat_panel.use_the_chat_below_for_live_support_the_contact')}</p>
        </div>
        <div className="flex flex-col items-end gap-2 text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-emerald-500" />
            Ready
          </div>
          {connectedAgent && (
            <div className="rounded-full border border-emerald-500/20 bg-emerald-500/5 px-3 py-1 text-emerald-300 tracking-[0.16em]">
              {connectedAgent.name} · {connectedAgent.role}
            </div>
          )}
        </div>
      </div>

      <div className={`grid gap-0 ${compact ? "lg:grid-cols-[1.15fr_0.85fr]" : "lg:grid-cols-[1.05fr_0.95fr]"}`}>
        <div className="p-5 border-b lg:border-b-0 lg:border-r border-zinc-800 min-h-[360px] flex flex-col">
          {loading ? (
            <div className="flex-1 min-h-[260px] flex items-center justify-center text-zinc-500 gap-2 font-mono text-[10px] uppercase tracking-[0.2em]">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading support chat
            </div>
          ) : (
            <div className="flex flex-col flex-1 min-h-0">
              <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/5 px-4 py-3 text-sm text-zinc-200 leading-relaxed mb-4">
                <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-400 mb-2">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Support ready
                </div>
                <p>{messages.length ? "Your support thread is active. Keep the conversation here for follow-up." : "Start your support conversation here and we’ll keep the thread active for follow-up."}</p>
              </div>

              <div className="flex-1 min-h-0 max-h-[380px] overflow-auto pr-1 space-y-3">
                {messages.map((message) => {
                  const isAssistant = message.sender === "assistant";
                  return (
                    <div key={message.message_id} className={`flex ${isAssistant ? "justify-start" : "justify-end"}`}>
                      <div className={`max-w-[92%] rounded-2xl px-4 py-3 border ${isAssistant ? "border-zinc-800 bg-zinc-900/60" : "border-amber-500/25 bg-amber-500/10"}`}>
                        <div className="flex items-center justify-between gap-3 mb-1.5">
                          <span className={`font-mono text-[10px] uppercase tracking-[0.2em] ${isAssistant ? "text-amber-500" : "text-zinc-300"}`}>
                            {message.author_name || (isAssistant ? "Max OS" : "You")}
                          </span>
                          <span className="font-mono text-[10px] text-zinc-600">{formatTime(message.created_at)}</span>
                        </div>
                        <p className="text-sm leading-relaxed text-zinc-100 whitespace-pre-wrap">{message.content}</p>
                      </div>
                    </div>
                  );
                })}
                {sending && (
                  <div className="flex justify-start">
                    <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 px-4 py-3 text-sm text-zinc-400 flex items-center gap-2">
                      <Loader2 className="w-3.5 h-3.5 animate-spin text-amber-500" /> Agent is replying...
                    </div>
                  </div>
                )}
                <div ref={endRef} />
                {!messages.length && (
                  <div className="rounded-2xl border border-dashed border-zinc-800 p-5 text-sm text-zinc-400">
                    No messages yet. Start by describing what you need help with.
                  </div>
                )}
              </div>

              <div className="mt-4 rounded-2xl border border-zinc-800 bg-zinc-950/70 p-4">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                  rows={compact ? 4 : 5}
                  placeholder={t('support_chat_panel.tell_us_what_you_need_help_with')}
                  className="w-full rounded-2xl border border-zinc-800 bg-zinc-950/90 px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-amber-500/60"
                />
                <div className="mt-3 flex justify-end">
                  <button
                    type="button"
                    onClick={() => sendMessage()}
                    disabled={sending || !input.trim()}
                    className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.22em] text-black transition-colors hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-50"
                    aria-label={t('support_chat_panel.send_message')}
                    title={t('support_chat_panel.send_message')}
                  >
                    {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="p-5 space-y-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">What helps us respond faster</div>
            <div className="space-y-3 text-sm text-zinc-300">
              {supportGuidance.map((item) => (
                <div key={item} className="rounded-2xl border border-zinc-800 bg-zinc-950/60 p-4 flex gap-3 items-start">
                  <Sparkles className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                  <p className="leading-relaxed">{item}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-zinc-800 bg-zinc-950/60 p-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-500 mb-2">Support reminder</div>
            <p className="text-sm text-zinc-300 leading-relaxed">
              If you want the support team to continue in a different channel, open the matching card above and include the same customer details.
            </p>
          </div>

          {error && (
            <div className="rounded-2xl border border-red-500/30 bg-red-500/5 px-4 py-3 text-sm text-red-200">
              {error}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
